import { MAX_TOPICS_PER_POST } from "@circle-react/components/QuickPostV2/Content/TopicsPicker/constants";
import { useComposerSpace } from "@circle-react/components/QuickPostV3/hooks/useComposerSpace";
import { useTopicsMultiSelect } from "@circle-react/components/QuickPostV3/hooks/useTopicsMultiSelect";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { MultiSelect } from "@circleco/compass/components/MultiSelect";
import { type KeyboardEvent, useState } from "react";
import { useController, useFormContext } from "react-hook-form";

import { t } from "@/i18n-js/instance";

interface TopicsProps {
  className?: string;
  inline?: boolean;
}

export const Topics = ({ className, inline }: TopicsProps = {}) => {
  const { spaceId, canManageSelectedSpace } = useComposerSpace();
  const { control } = useFormContext();
  const { field } = useController({ control, name: "topics" });
  const [isOpen, setIsOpen] = useState(false);

  const selectedIds = (field.value as number[] | undefined) ?? [];

  const {
    createTopic,
    isCreatable: canCreate,
    dropdownOptions,
    handleValueChange,
    isLoading,
    onInputValueChange,
    selectedOptions,
    shouldOpenDropdown,
    shouldShow,
  } = useTopicsMultiSelect({
    spaceId: spaceId ? String(spaceId) : undefined,
    value: selectedIds,
    onChange: field.onChange,
    isCreatable: canManageSelectedSpace,
  });

  const isDropdownOpen = isOpen && shouldOpenDropdown;

  if (!spaceId) return null;
  if (!shouldShow) return null;

  const handleKeyDownCapture = (event: KeyboardEvent) => {
    if (event.key !== "Enter" || isDropdownOpen) return;
    event.preventDefault();
  };
  const handleInputValueChange = (nextValue: string) => {
    onInputValueChange(nextValue);
    setIsOpen(true);
  };

  return (
    <div
      className={classNames(
        "text-primary font-label-xs flex w-full min-w-0 px-4 bg-secondary lg:bg-transparent lg:px-0",
        className,
        !inline && "py-2",
        inline && "min-w-0",
      )}
      onKeyDownCapture={handleKeyDownCapture}
    >
      <MultiSelect<number>
        data-testid="topics-list"
        className="flex-1"
        naked
        icon="tag"
        open={isDropdownOpen}
        onOpenChange={setIsOpen}
        side="top"
        align="start"
        options={dropdownOptions}
        value={selectedOptions}
        onValueChange={handleValueChange}
        onInputValueChange={handleInputValueChange}
        isLoading={isLoading}
        maxSelections={MAX_TOPICS_PER_POST}
        placeholder={t("quick_post_v2.topics.search", {
          count: MAX_TOPICS_PER_POST,
        })}
        searchPlaceholder={t("search_v2.filters.search_topics")}
        emptyMessage={t("quick_post_v2.topics.no_results")}
        creatable={canCreate}
        onCreateOption={name => createTopic(name)}
        loadingMessage={t("quick_post_v2.topics.loading")}
      />
    </div>
  );
};
